import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { Input, Textarea, Label } from "../ui-custom/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui-custom/Select";
import { Receipt, Upload } from "lucide-react";

const projects = ["Sunrise Apartments", "Green Valley Villas", "Metro Heights", "Tech Park Phase 2"];
const categories = ["Materials", "Labour", "Transport", "Tools", "Miscellaneous"];
const paymentTypes = ["Cash", "UPI", "Bank Transfer"];

export function PettyCashExpenses({
  employees,
  onAddExpense,
}) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [project, setProject] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [remarks, setRemarks] = useState("");

  const amountNum = parseFloat(amount) || 0;
  const needsApproval = amountNum > 2000;

  const handleSubmit = () => {
    if (!title || !amount || !project || !paymentType) return;

    const currentEmployee = employees[0];

    onAddExpense({
      employeeId: currentEmployee.id,
      employeeName: currentEmployee.name,
      title,
      amount: amountNum,
      project,
      category,
      paymentType: paymentType === "Cash" ? "Cash" : "Online",
      date: new Date().toISOString().split("T")[0],
      remarks: remarks || undefined,
    });

    // Reset form
    setTitle("");
    setAmount("");
    setCategory("");
    setProject("");
    setPaymentType("");
    setRemarks("");
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-sm-accent/10 rounded">
              <Receipt className="w-4 h-4 text-sm-accent" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Submit Expense</CardTitle>
              <p className="text-sm text-text-gray">
                Record your site expense here. Small expenses (up to ₹2,000) are approved automatically!
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-5">
          {/* Title */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-primary-text">What did you spend on?</Label>
            <Input
              placeholder="e.g., Cement bags, Daily wages, Tools..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Amount & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary-text">Amount (₹)</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary-text">Category</Label>
              <Select value={category} onValueChange={setCategory} placeholder="Select">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Project & Payment Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary-text">Project</Label>
              <Select value={project} onValueChange={setProject} placeholder="Select project">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary-text">Payment Type</Label>
              <Select value={paymentType} onValueChange={setPaymentType} placeholder="How did you pay?">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentTypes.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Attach Bill */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-primary-text">Attach Bill (optional but helpful)</Label>
            <div className="border-2 border-dashed border-sm-border rounded-lg p-6 text-center cursor-pointer hover:bg-gray-bg/30 transition-colors">
              <Upload className="w-6 h-6 mx-auto text-text-gray mb-2" />
              <p className="text-sm text-text-gray">Tap to upload bill photo</p>
            </div>
          </div>

          {/* Remarks */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-primary-text">Remarks (optional)</Label>
            <Textarea
              placeholder="Any additional details..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Approval Notice */}
          {needsApproval && (
            <div className="bg-sm-accent/10 border border-sm-accent/20 rounded-lg p-3 text-sm text-sm-accent font-medium">
              This expense is above ₹2,000 and will need approval from finance. Don't worry, we'll handle it!
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!title || !amount || !project || !paymentType}
            className="w-full h-11"
            variant="accent"
          >
            {needsApproval ? "Submit for Approval" : "Submit Expense"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
